// UserManagementTable.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import axiosInstance from "../../utils/axiosInstance";
import UserForm from "../user/UserForm";
import apiConfig from "../../config/appConfig";
import { format } from "date-fns";
import { DATE_FORMAT_AM_PM } from "../../utils/constants";

const UserDataTable = () => {
  const [rows, setRows] = useState([]); // User data
  const [loading, setLoading] = useState(false); // Loading state
  const [totalRows, setTotalRows] = useState(0); // Total rows count
  const [searchText, setSearchText] = useState(""); // Search text
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid uses 0-based indexing
    pageSize: 10, // Default rows per page
  });
  const [formOpen, setFormOpen] = useState(false); // Modal state for Add/Edit User
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  }); // Snackbar for notifications
  const [selectedUser, setSelectedUser] = useState(null); // Define selectedUser
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null); // Anchor element for the menu
  const [menuRow, setMenuRow] = useState(null); // Row associated with the menu
  const [sortModel, setSortModel] = useState([
    { field: "modifiedDate", sort: "desc" }, // Default sorting
  ]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(apiConfig.endpoints.users.paginated, {
        params: {
          page: paginationModel.page + 1, // Convert 0-based to 1-based indexing for backend
          limit: paginationModel.pageSize,
          search: searchText,
          sortField: sortModel[0]?.field || "modifiedDate",
          sortOrder: sortModel[0]?.sort || "desc",
        },
      });

      setRows(response.data.users);
      setTotalRows(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching users:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch users.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchText, sortModel]);

  const handleSortModelChange = (model) => {
    setSortModel(model); // Update sort model
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchorEl(event.currentTarget); // Set the anchor element
    setMenuRow(row); // Set the associated row
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null); // Clear the anchor element
    setMenuRow(null); // Clear the associated row
  };

  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // Delete a user
  const handleConfirmDelete = async () => {
    try {
      if (userToDelete) {
        await axiosInstance.delete(apiConfig.endpoints.users.byId(userToDelete.userId));
        setSnackbar({
          open: true,
          message: "User deleted successfully!",
          severity: "success",
        });
        fetchUsers(); // Refresh data
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete user.",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false); // Close dialog
      setUserToDelete(null); // Reset selected user
    }
  };

  // Open Add/Edit Form
  const handleOpenForm = (user = null) => {
    setSelectedUser(user); // Set the selected user for editing or null for adding
    setFormOpen(true);
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchText(event.target.value);
  };

  // Close Snackbar
  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return; // Ignore clickaway events
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Define columns for DataGrid
  const columns = [
    { field: "userId", headerName: "User ID", flex: 0.5 },
    { field: "username", headerName: "Username", flex: 1 },
    { field: "role", headerName: "Role", flex: 1 },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 200,
      valueFormatter: (date) => {
        if (!date) return ""; // Handle null or undefined params
        try {
          return format(new Date(date), DATE_FORMAT_AM_PM); // Safely format the date
        } catch (error) {
          console.error("Invalid date format:", date);
          return "Invalid Date"; // Fallback for invalid dates
        }
      },
    },
    {
      field: "modifiedDate",
      headerName: "Modified Date",
      width: 200,
      valueFormatter: (date) => {
        if (!date) return ""; // Handle null or undefined params
        try {
          return format(new Date(date), DATE_FORMAT_AM_PM); // Safely format the date
        } catch (error) {
          console.error("Invalid date format:", date);
          return "Invalid Date"; // Fallback for invalid dates
        }
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const statusColors = {
          active: "success",
          inactive: "default",
        };

        return (
          <Chip
            label={params.row.status || "Unknown"} // Fallback to 'Unknown' if status is null or undefined
            color={statusColors[params.row.status] || "default"} // Use 'default' if the status is not in the mapping
            size="small"
            sx={{
              fontWeight: "bold",
              textTransform: "capitalize", // Make the label look cleaner
              borderRadius: 1, // Less rounded corners
              padding: "0 8px", // Adjust padding for better shape
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            aria-label="more"
            aria-controls="actions-menu"
            aria-haspopup="true"
            onClick={(event) => handleMenuOpen(event, params.row)}
          >
            <MoreVertIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      {/* Table Title and Search */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Typography variant="h4">Users</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearch}
            placeholder="Search by username"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()} // Open Add Form
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* DataGrid */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            height: "70vh",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
            width: "100%",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            disableSelectionOnClick
            pagination
            getRowId={(row) => row.userId}
            pageSizeOptions={[10, 25, 50, 100]}
            rowsPerPageOptions={[10, 25, 50, 100]}
            paginationModel={paginationModel} 
            onPaginationModelChange={(newModel) => {
              console.log("Pagination model changed:", newModel); // Debugging
              setPaginationModel(newModel); // Update pagination state
            }}
            rowCount={totalRows}
            onSortModelChange={handleSortModelChange}
            paginationMode="server"
            sortModel={sortModel}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f4f6f8",
                fontWeight: "bold",
              },
            }}
            components={{
              NoRowsOverlay: () => (
                <Box sx={{ textAlign: "center", padding: 2 }}>
                  <p>No users found</p>
                </Box>
              ),
            }}
          />

          {/* Actions Menu */}
          <Menu
            id="actions-menu"
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() => {
                handleOpenForm(menuRow);
                handleMenuClose();
              }}
            >
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleOpenDeleteDialog(menuRow);
                handleMenuClose();
              }}
            >
              Delete
            </MenuItem>
          </Menu>
        </Box>
      )}

      {/* Add/Edit User Form */}
      <UserForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initialValues={selectedUser || {}}
        isUpdate={!!selectedUser}
        refreshUsers={fetchUsers}
        setSnackbar={setSnackbar} // Pass the setSnackbar function from the parent
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{userToDelete?.username}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDataTable;